import React, { useState, useEffect, FC } from 'react'
import { Link } from 'react-router-dom'
import { RadioButton, RadioButtonGroup } from '@ynap/radio-button'
import { SimpleAsyncButton } from '@ynap/button'
import { CombinedSelect } from '@ynap/select'
import Address from '@ynap/address'
import BEMHelper from '@ynap/bem-helper'
import Notification from '@ynap/notification'
import getPickupAvailability from './fetchUtils/getPickupAvailability'
import { IShippingAddress } from './types/index'
import createShipments from './fetchUtils/createDHLPickUps'
import createPickUps from './fetchUtils/createUPSPickUps'
import { useMessages } from '@ynap/immutables-provider'
import track from 'react-tracking'
import {
    trackReturnCollectionComplete,
    trackReturnCollectionSkipped,
    trackSelectReturnMethod,
    trackDropOffComplete,
    trackDropOffLocator
} from '../analytics'
import Skeleton from '@ynap/skeleton'
import { Button } from '@ynap/button'
import { IGetPickupAvailability } from './types/IGetPickupAvailability'

const bem = new BEMHelper('CreateCollection')

interface IcreateCollectionProps {
    config: any,
    shippingAddress: IShippingAddress,
    returnCourierTrackingId: string,
    messages: any,
    locale: any,
    externalOrderId: string,
    urlPath: string,
    push: (string) => void,
    returnItems: any[],
    tracking: {
        getTrackingData: () => void
        trackEvent: (event: any) => void
    },
    dropOffLocatorLink: string,
    returnCourier: string,
    trackingUrl: string,
    returnTrackingUrl: string
}

const boxNumberOptions = [1, 2, 3, 4, 5].map(num => ({
    value: num,
    key: num,
    label: num
}));

const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']

type DateOption = {
    value: string
    label: string
}

const getDateOption = (date: Date, locale: string, messages?: any): DateOption => {
    let dateString = date.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })
    if (locale?.startsWith('ar-') && messages) {
        const [year, month, monthDay] = date.toISOString().slice(0, 10).split('-').map(it => parseInt(it, 10))
        const monthString = messages?.months[months[month - 1]]?.()
        const weekday = messages?.createCollection.timeSlot[weekdays[date.getDay()]]?.()
        dateString = `${weekday} ${monthDay} ${monthString} ${year}`
    }

    return {
        value: date.toDateString(),
        label: dateString
    }
}

export const createWeek = ({ date, dayOfWeek }, locale):DateOption[] => {
    const daysOptions: Array<DateOption> = [];
    const messages = useMessages()
    const dropDownOptions = 7

    let day
    let d
    let m
    let y
    d = date.getDate(),
    m = date.getMonth(),
    y = date.getFullYear()

    if (dayOfWeek === messages.createCollection.timeSlot.today()) {
        daysOptions.push({
            value: dayOfWeek,
            label: dayOfWeek
        })
    } else {
        const today: Date = new Date(y, m, d)
        daysOptions.push(getDateOption(today, locale, messages))
    }

    for (day = 1; day < dropDownOptions; day++) {
        const currDate: Date = new Date(y, m, d + day)
        daysOptions.push(getDateOption(currDate, locale, messages))
    }
    return daysOptions
}

const { telephone = '', email = '' } =
        (typeof window !== 'undefined' && window.SF?.getContactDetails?.()?.customerCare) || {}

const phone = `<a href="tel:${telephone}">${telephone}</a>`
const mail = `<a href="mailto:${email}">${email}</a>`


const CreateCollection: FC<IcreateCollectionProps> =({ config, shippingAddress, locale, externalOrderId, urlPath, push, returnItems, tracking, dropOffLocatorLink, returnCourier, trackingUrl, returnTrackingUrl }) => {
    const [returnOption, setReturnOption] = useState<string>("")
    const [numberOfBoxes, setNumberOfBoxes] = useState<number>(1)
    const [collectionDay, setCollectionDay] = useState<string | undefined>(undefined)
    const [availability, setAvailability] = useState<IGetPickupAvailability>({
        timePeriod: undefined,
        datePeriod: undefined,
        plannedPickupDateAndTime: undefined,
        missedCutOff: undefined,
        error: false,
        productCode: undefined,
        dateRequested: undefined,
        isPlannedPickupRequestedDate: undefined
    })
    const [loading, setLoading] = useState<boolean>(false)
    const [userInputDateLoading, setUserInputDateLoading] = useState<boolean>(false)
    const [specialInstructions, setSpecialInstructions] = useState<string>("")
    const [createError, setCreateError] = useState<boolean | string>(false)
    const [validationError, setValidationError] = useState<boolean>(false)
    const [startOfWeek, setStartOfWeek] = useState<{ date: Date | undefined, dayOfWeek: (string | undefined) } | undefined>(undefined)

    const confirmationUrl = `/${locale.id}/account/${urlPath}/${externalOrderId}/return/confirmation`
    
    const messages = useMessages()

    const pickUpOptions = startOfWeek && createWeek(startOfWeek, locale.id).map(day => ({
        value: day?.value, //Sun Feb 28 2021
        key: day?.value,
        label: day?.label
    }))

    useEffect(() => { tracking.trackEvent(trackSelectReturnMethod(externalOrderId, locale)) }, [])
    useEffect(() => {
        getPickupAvailability(config, messages, shippingAddress, undefined, returnItems, returnCourier)
            .then(
                (availability: IGetPickupAvailability) => {
                    setAvailability(availability)
                    !availability.error && setStartOfWeek({ date: availability.dateRequested, dayOfWeek: availability.datePeriod })
                }
            )
    }, [])

    const submitCreateRequest = () => {
        // NOTE: This condition doesn't have any async behaviour e.g API Call, so to mock the illusion
        // of loading state with the dots animation, have wrapped this in a 1s timeout
        if (returnOption === 'dropOff') {
            setTimeout(() => {
                setLoading(false)
                push(`${confirmationUrl}?id=drop-confirmation`)
                tracking.trackEvent(trackDropOffComplete(returnItems, externalOrderId, locale, returnCourier))
            }, 1000)
        }
        else if (returnCourier === 'DHL') {
            createShipments(config, shippingAddress, availability.plannedPickupDateAndTime, numberOfBoxes, specialInstructions, returnItems, externalOrderId, availability.productCode, trackingUrl, returnTrackingUrl)
                .then(({ dispatchConfirmationNumbers }) => {
                    setLoading(false)
                    dispatchConfirmationNumbers.length > 0 &&
                        tracking.trackEvent(trackReturnCollectionComplete(returnItems, externalOrderId, locale, returnCourier))
                    push(`${confirmationUrl}?id=${dispatchConfirmationNumbers[0]}&timePeriod=${availability.timePeriod}&datePeriod=${availability.datePeriod}&parcels=${numberOfBoxes}`)
                }).catch((e) => {
                    setLoading(false)
                    if (e?.message?.includes('5001')) /* time slot error */ {
                        setCreateError('timeSlotError')
                    } else {
                        setCreateError(true)
                    }  
                })
        }
        else if (returnCourier === 'UPS') {
            createPickUps(config, shippingAddress, availability.plannedPickupDateAndTime, numberOfBoxes, specialInstructions, availability.productCode, trackingUrl, returnTrackingUrl, externalOrderId).then(({ PickupCreationResponse: { PRN }, }) => {
                setLoading(false)
                PRN &&
                    tracking.trackEvent(trackReturnCollectionComplete(returnItems, externalOrderId, locale,returnCourier))
                push(`${confirmationUrl}?id=${PRN}&timePeriod=${availability.timePeriod}&datePeriod=${availability.datePeriod}&parcels=${numberOfBoxes}`)
            }).catch((e) => {
                setLoading(false)
                setCreateError(true)
            })
        }
    }

    const isPickupDateAvailable = (startOfWeek?.dayOfWeek === availability.datePeriod || availability.isPlannedPickupRequestedDate)
    const courierSlotMessaging = () => {
        return isPickupDateAvailable ? (
            <p className={bem('courierSlot')}>{availability.datePeriod} {availability.timePeriod}</p>
        ) : (
                <Notification
                    message={messages.createCollection.chosenDayNotAvailable()}
                    type="error"
                    className={bem('chosenDayError')}
                />
            )
    }
    return (
        <div className={bem(null)}>
            <div className={bem('pickUpInfo')}>
                <h2 className={bem('pickUpTitle')}>{messages.createCollection.title()}</h2>
            </div>
            <p className={bem('pickUpSubTitle')}>{messages.createCollection.subTitle()}</p>


            <RadioButtonGroup
                className={bem('courierOptions')}
                value={returnOption}
                id="returnOption"
                name="returnOption"
                onChange={(e) => {
                    e.stopPropagation();
                    // TODO: Explore changing combined select to native html select to control nesting of select/radio button
                    if (e.target.value === 'collection' || e.target.value === 'dropOff') {
                        setReturnOption(e.target.value)
                    }
                }}
            >
                <RadioButton className={bem('pickUp')} value="collection" label={messages.createCollection.optionCollection()} />

                {
                    returnOption === "collection" && !availability.error ? (
                        <div className={bem('collectionDetails')}>
                            {!collectionDay ?
                                <p>{messages.createCollection.courierTimingMessage({ returnCourier })}</p>
                                :
                                <p>{messages.createCollection.courierTimingSelectedMessage()}</p>
                            }

                            {availability.missedCutOff || !availability.datePeriod || userInputDateLoading ?
                                (
                                    <Skeleton className={bem('collectionDetailsSkeleton')} />
                                ) :
                                (
                                    courierSlotMessaging()
                                )
                            }

                            <p className={bem('selection')}>{messages.createCollection.chooseAnotherDay()}</p>
                                    <CombinedSelect
                                        type="defaultLink"
                                        disabled={availability.missedCutOff || !availability.datePeriod}
                                        value={collectionDay}
                                        className={bem('dayOptions')}
                                        onChange={(value: string) => {
                                            const userInputDate = value !== messages.createCollection.timeSlot.today() ? new Date(value) : undefined
                                            setCollectionDay(value)
                                            setUserInputDateLoading(true)
                                            getPickupAvailability(config, messages, shippingAddress, userInputDate, returnItems, returnCourier).then(availability => { setAvailability(availability); setUserInputDateLoading(false) })
                                        }}
                                        options={pickUpOptions}
                                        emptyLabel={messages.createCollection.selectDateLabel()}
                                        name="dayOptions"
                                    />
                            <p className={bem('addressMessage')}>{messages.createCollection.addressMessage()}</p>
                            <Address className={bem('address')} {...shippingAddress} />
                            <p className={bem('specialInformation')}>{messages.createCollection.specialInformationMessage({ returnCourier })}</p>
                            <textarea className={bem('input', validationError ? 'error' : null)} value={specialInstructions} onChange={e => {
                                e.target.value.length > 57 ? setValidationError(true) : setValidationError(false) // max field length for UPS is 57 characters
                                setSpecialInstructions(e.target.value)
                            }} />
                            {validationError &&
                                <Notification
                                    type="field-error"
                                    message={messages.createCollection.maxLength({ characters: '57' })}
                                />
                            }
                            <p className={bem('callCustomerCare')} dangerouslySetInnerHTML={{ __html: messages.createCollection.contactCustomerCare({ phone, mail }) }}></p>
                            <p className={bem('selection')}>{messages.createCollection.boxQuantityMessage()}</p>

                            <CombinedSelect
                                type="defaultLink"
                                value={numberOfBoxes}
                                className={bem('numberOfBoxes')}
                                onChange={(value: number) => setNumberOfBoxes(value)}
                                options={boxNumberOptions}
                                emptyLabel={numberOfBoxes}
                                name="boxQuantitiy"
                            />
                        </div>
                    ) : (
                            returnOption === "collection" && availability.error && (
                                <Notification
                                    type="error"
                                    message={messages.createCollection.error({ returnCourier, createError })}
                                    className={bem('error')}
                                />
                            )
                        )
                }
                <RadioButton className={bem('dropOff')} value="dropOff" label={messages.createCollection.optionDropOff({ returnCourier })} />
                {returnOption === 'dropOff' && <Button
                    type="link"
                    component="a"
                    href={dropOffLocatorLink}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={bem('courierServicePoint')}
                    onClick={() => {
                        tracking.trackEvent(trackDropOffLocator(returnCourier))
                    }}
                >
                    {messages.createCollection.courierServicePoint({ returnCourier })}
                </Button>}
            </RadioButtonGroup>
            <SimpleAsyncButton
                className={bem('toComplete', !returnOption ? 'disabled' : null)}
                type="primary"
                disabled={!returnOption || ((returnOption === 'collection' && availability.error) || (returnOption === 'collection' && !isPickupDateAvailable) || (returnOption === 'collection' && validationError))}
                copy={messages.createCollection.submit()}
                onClick={() => {
                    setLoading(true)
                    submitCreateRequest()
                }}
                loading={loading}
            />
            {createError &&
                <Notification
                    type="error"
                    message={messages.createCollection.error({ returnCourier, createError })}
                    className={bem('createError')}
                />
            }
            <Link
                className={bem('skipReturnLink')}
                to={confirmationUrl}
                onClick={() => {
                    tracking.trackEvent(trackReturnCollectionSkipped(externalOrderId, locale))
                }}
            >
                {messages.createCollection.skipReturn()}
            </Link>

        </div>
    )
}

export default track()(CreateCollection)
